'use client'

import React, { useState } from 'react'
import { apiSubscribeNewsletter } from '@/apis/page_optimization'
import { apiVerifyCaptcha } from '@/apis/captcha'
import { RecaptchaProvider } from '@/components/shared/RecaptchaProvider'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { toast } from 'react-toastify'
import { ArrowRight, Leaf, CheckCircle } from 'lucide-react'

interface Props { companyId: string; domain?: string }

const NewsletterInner = ({ companyId, domain }: Props) => {
    const [email, setEmail] = useState('')
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const { executeRecaptcha } = useGoogleReCaptcha()

    const validateEmail = (emailStr: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return regex.test(emailStr)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!email.trim()) {
            toast.error('Email field is required.', { autoClose: 2000 })
            return
        }

        if (!validateEmail(email)) {
            toast.error('Please enter a valid email address.', { autoClose: 2000 })
            return
        }

        setLoading(true)

        try {
            // Step 1: Verify token using domain if recaptcha is available
            if (executeRecaptcha && domain) {
                try {
                    const recaptchaToken = await executeRecaptcha('newsletter_form')
                    if (recaptchaToken) {
                        const captchaRes = await apiVerifyCaptcha(domain, recaptchaToken)
                        if (captchaRes && captchaRes.verified === false) {
                            toast.error('Captcha verification failed. Please try again.', { autoClose: 2000 })
                            setLoading(false)
                            return
                        }
                    }
                } catch (recaptchaErr) {
                    console.warn('reCAPTCHA skipped or failed:', recaptchaErr)
                }
            }

            // Step 2: Subscribe newsletter using companyId
            const response = await apiSubscribeNewsletter(companyId, email)
            const resMsg = typeof response?.message === 'string' ? response.message.trim() : ''
            const resStatus: any = response?.status

            const isSuccess =
                (
                    resStatus === true ||
                    resStatus === 'true' ||
                    resStatus === 200 ||
                    resStatus === '200' ||
                    resStatus === 201 ||
                    resStatus === '201' ||
                    (response as any)?.success === true ||
                    resMsg.toLowerCase().includes('subscribed successfully') ||
                    resMsg.toLowerCase().includes('subscriber successfully added') ||
                    resMsg.toLowerCase().includes('thank you') ||
                    resMsg.toLowerCase().includes('success') ||
                    resMsg.toLowerCase().includes('already') ||
                    resMsg.toLowerCase().includes('exists') ||
                    resMsg.toLowerCase().includes('taken')
                ) &&
                !resMsg.includes('Server responded with status') &&
                !resMsg.toLowerCase().includes('fail') &&
                !resMsg.toLowerCase().includes('not found')

            if (isSuccess) {
                toast.success('Thank you for subscribing!', { autoClose: 2500 })
                setEmail('')
                setSuccess(true)
                setTimeout(() => setSuccess(false), 3000)
            } else if (resMsg && resMsg !== '{}' && !resMsg.includes('Server responded with status')) {
                toast.error(resMsg, { autoClose: 2500 })
            } else {
                toast.error('Failed to subscribe. Please try again later.', { autoClose: 2500 })
            }
        } catch (error) {
            console.error('Error subscribing:', error)
            toast.error('Failed to subscribe. Please try again later.', { autoClose: 2500 })
        } finally {
            setLoading(false)
        }
    }

    return (
        <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #F5C542 0%, #e5b026 50%, #d49f19 100%)' }} aria-labelledby="newsletter-heading">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden select-none">
                <span className="text-[20vw] font-black opacity-[0.08] whitespace-nowrap" style={{ color: '#172717', fontFamily: 'Georgia, serif' }}>
                    HERBAL
                </span>
            </div>
            {/* Ambient glows */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-950/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative max-w-6xl mx-auto px-6 py-24">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[10px] font-extrabold uppercase tracking-[0.2em] mb-6 shadow-sm"
                            style={{ background: 'rgba(23,39,23,0.1)', color: '#172717', border: '1px solid rgba(23,39,23,0.15)' }}>
                            <Leaf size={12} className="text-[#172717]" /> Free Deals Weekly
                        </div>
                        <h2 id="newsletter-heading" className="font-black leading-tight mb-6 tracking-tight drop-shadow-sm"
                            style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontFamily: 'Georgia, serif', color: '#172717' }}>
                            Never Miss a<br />
                            <span style={{ color: '#FAF7F2' }}>Herbal Deal</span><br />
                            Again.
                        </h2>
                        <p className="text-base sm:text-lg font-medium leading-relaxed" style={{ color: 'rgba(23,39,23,0.85)' }}>
                            Join 50,000+ wellness enthusiasts getting exclusive verified herbal deals delivered every week.
                        </p>
                    </div>
                    <div>
                        <form onSubmit={handleSubmit} className="space-y-4 backdrop-blur-md p-8 rounded-3xl" style={{ background: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.4)', boxShadow: '0 10px 35px rgba(0,0,0,0.08)' }} aria-label="Newsletter signup">
                            <div>
                                <label htmlFor="nl-email" className="block text-xs font-black uppercase tracking-widest mb-2.5"
                                    style={{ color: '#172717' }}>
                                    Your Email
                                </label>
                                <input id="nl-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                                    placeholder="you@example.com" required
                                    className="w-full px-6 py-4 rounded-2xl text-base font-medium focus:outline-none transition-all placeholder:text-[rgba(23,39,23,0.5)] shadow-inner"
                                    style={{ background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(23,39,23,0.15)', color: '#172717' }} />
                            </div>
                            <button type="submit" disabled={loading}
                                className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl font-black text-base disabled:opacity-60 hover:-translate-y-0.5 hover:shadow-xl transition-all duration-300"
                                style={{ background: '#172717', color: '#FAF7F2', boxShadow: '0 6px 20px rgba(23,39,23,0.3)' }}>
                                {loading ? 'Joining...' : success ? <><CheckCircle size={18} /> Subscribed!</> : <>Subscribe <ArrowRight size={18} /></>}
                            </button>
                        </form>
                        <p className="text-xs mt-4 text-center font-medium" style={{ color: 'rgba(23,39,23,0.7)' }}>No spam. Unsubscribe anytime.</p>
                    </div>
                </div>
            </div>
        </section>
    )
}

const Newsletter = (props: Props) => (
    <RecaptchaProvider domain={props.domain || ''}>
        <NewsletterInner {...props} />
    </RecaptchaProvider>
)

export default Newsletter
